import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  GlobalStyles,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  AccessTime as AccessTimeIcon,
  ArrowBack as ArrowBackIcon,
  Bolt as BoltIcon,
  CalendarMonth as CalendarMonthIcon,
  ChecklistRounded as ChecklistRoundedIcon,
  Language as LanguageIcon,
  LocationOn as LocationOnIcon,
  Send as SendIcon,
  WhatsApp as WhatsAppIcon,
  WorkOutline as WorkOutlineIcon,
} from '@mui/icons-material';
import { usePostHog } from '@posthog/react';
import { useAuth } from '../services/AuthService';
import ApplicationService from '../services/ApplicationService';
import JobService from '../services/JobService';
import { useUserType } from '../hooks/useUserType';
import { formatWhatsAppTime } from '../utils/dateUtils';

const REQUIREMENT_LABELS = {
  white_card: 'White card required',
  ppe_required: 'PPE required',
  own_tools_required: 'Own tools required',
  vehicle_required: 'Own vehicle preferred',
  drivers_license: 'Driver licence required',
  work_rights_required: 'Work rights required',
};

const MISSING_INFO_LABELS = {
  salary: 'pay details',
  salary_rate: 'pay details',
  start_date: 'start date',
  start_time: 'start time',
  visa_status: 'visa requirements',
  english_level: 'language requirements',
  tickets: 'tickets or licences',
  location: 'exact location',
  duration: 'job duration',
  contact_name: 'contact name',
};

const TRANSLATION_OPTIONS = [
  { key: 'ptBr', label: 'Portuguese' },
  { key: 'es', label: 'Spanish' },
  { key: 'fr', label: 'French' },
];

const SECTION_EYEBROW_SX = {
  letterSpacing: 1.2,
  color: 'secondary.main',
  fontWeight: 800,
  display: 'block',
  mb: 0.5,
};

const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const parsed = Number(String(value).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
};

const humanizeToken = (value) =>
  String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());

const getFirstMeaningfulLine = (description) =>
  String(description || '')
    .split('\n')
    .map((line) => line.replace(/[*_`]/g, '').trim())
    .find((line) => line.length >= 4) || '';

const extractLocationFromDescription = (description) => {
  const suburbMatch = String(description || '').match(
    /(?:📍|location[:\s-]*|in\s+)([A-Za-z0-9\s.'-]{3,40}?)(?:,\s*(NSW|VIC|QLD|WA)\b|\n|$)/i
  );
  const stateMatch = String(description || '').match(/\b(NSW|VIC|QLD|WA|SA|TAS|NT|ACT)\b/i);

  return {
    suburb: suburbMatch?.[1]?.trim() || '',
    state: stateMatch?.[1]?.toUpperCase() || '',
  };
};

const formatRate = (rate, unit) => {
  const numericRate = toNumber(rate);
  if (numericRate === null) return null;

  const normalizedUnit = String(unit || 'hr').toLowerCase();
  const shortUnit = normalizedUnit.startsWith('hour')
    ? 'hr'
    : normalizedUnit.startsWith('day')
      ? 'day'
      : normalizedUnit.startsWith('week')
        ? 'wk'
        : normalizedUnit;

  return `$${Number(numericRate).toFixed(Number.isInteger(numericRate) ? 0 : 2)}/${shortUnit}`;
};

const formatDateLabel = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(parsed);
};

const formatStartDateLabel = (scheduleItem) =>
  scheduleItem?.start_date ? formatDateLabel(scheduleItem.start_date) : null;

const buildJobPresentation = (job) => {
  if (!job) return null;

  const enriched = job.enrichedData || {};
  const salary = enriched.salary || {};
  const requirements = enriched.requirements || {};
  const location = Array.isArray(enriched.location) ? enriched.location[0] || {} : {};
  const scheduleItem = Array.isArray(enriched.schedule) ? enriched.schedule[0] || null : null;
  const fallbackLocation = extractLocationFromDescription(job.description);
  const translated = enriched.translatedDescription || {};
  const title =
    enriched.job_subcategory ||
    job.referenceCode ||
    getFirstMeaningfulLine(job.description) ||
    'Job opportunity';

  const suburb = location.suburb || fallbackLocation.suburb || '';
  const state = String(location.state || fallbackLocation.state || '').toUpperCase();
  const region = location.sub_region || location.metro_region || '';
  const locationLabel = [suburb, region, state].filter(Boolean).join(', ');
  const rateLabel = formatRate(salary.rate, salary.unit);
  const vacancies = toNumber(enriched.vacancies);
  const urgencyScore = toNumber(enriched.urgency_score);
  const companyName =
    job.onBehalfOfCompany || job.onBehalfOfCompanyName || job.company || 'Verified employer';
  const category = enriched.job_category || 'General labour';
  const translations = {
    ptBr: translated.ptBr || null,
    es: translated.es || null,
    fr: translated.fr || null,
  };
  const requirementsList = Object.entries(REQUIREMENT_LABELS)
    .filter(([key]) => requirements[key])
    .map(([, label]) => label);

  if (requirements.english_level) {
    requirementsList.push(`English: ${humanizeToken(requirements.english_level)}`);
  }

  return {
    title,
    companyName,
    category,
    locationLabel,
    suburb,
    state,
    region,
    rateLabel,
    vacancies,
    urgencyScore,
    startDateLabel: formatStartDateLabel(scheduleItem),
    originalDescription: job.description || '',
    translations,
    highlights: Array.isArray(enriched.highlights) ? enriched.highlights.filter(Boolean) : [],
    tickets: Array.isArray(enriched.tickets) ? enriched.tickets.filter(Boolean) : [],
    tags: Array.isArray(enriched.tags) ? enriched.tags.filter(Boolean) : [],
    missingInfo: Array.isArray(enriched.missing_information)
      ? enriched.missing_information.filter(Boolean).map((item) => MISSING_INFO_LABELS[item] || humanizeToken(item))
      : [],
    requirementsList,
  };
};

const SimpleMetaItem = ({ icon, label, value }) => {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Box
        sx={{
          flexShrink: 0,
          color: 'primary.main',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        {icon}
      </Box>
      <Typography variant="body2" color="text.secondary">
        <Box component="span" sx={{ fontWeight: 800, color: 'text.primary' }}>
          {label}:
        </Box>{' '}
        {value}
      </Typography>
    </Stack>
  );
};

const SectionCard = ({ eyebrow, title, children }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 2, md: 3 },
      borderRadius: 4,
      border: '1px solid',
      borderColor: 'divider',
      bgcolor: 'background.paper',
      boxShadow: '0 18px 40px rgba(7, 44, 22, 0.08)',
    }}
  >
    {(eyebrow || title) && (
      <Box sx={{ mb: 2 }}>
        {eyebrow ? (
          <Typography
            variant="overline"
            sx={SECTION_EYEBROW_SX}
          >
            {eyebrow}
          </Typography>
        ) : null}
        {title ? (
          <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.05 }}>
            {title}
          </Typography>
        ) : null}
      </Box>
    )}
    {children}
  </Paper>
);

const generateApplicationMessage = (jobData, profileData) => {
  let message = 'Hi, I am interested in the job you posted on Labourando.\n\n';
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const publicProfileUrl = `${origin}/jobseeker/public/${profileData.id}?jobId=${jobData.id}`;
  message += `You can view my full profile, experience, and skills here: ${publicProfileUrl}\n\n`;

  if (profileData?.phone) {
    message += `My contact number is: ${profileData.phone}\n\n`;
  }

  message += 'Thanks,\n';

  const fullName = [profileData?.firstName, profileData?.lastName].filter(Boolean).join(' ');
  if (fullName) {
    message += fullName;
  } else {
    message += 'Candidate';
  }

  if (jobData?.referenceCode) {
    message += `\n\nJob Ref: ${jobData.referenceCode}`;
  }

  return message;
};

const JobApply = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id: jobId } = useParams();
  const posthog = usePostHog();
  const { isAuthenticated, loginWithRedirect, signupWithRedirect, user } = useAuth();
  const { isCompany, userTypeData, loading: userTypeLoading, isProfileDefined } = useUserType();

  const [job, setJob] = useState(null);
  const [jobSeekerProfile, setJobSeekerProfile] = useState(null);
  const [applicationMessage, setApplicationMessage] = useState('');
  const [loadError, setLoadError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [showErrorAnimation, setShowErrorAnimation] = useState(false);
  const [isJobLoading, setIsJobLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOpeningWhatsApp, setIsOpeningWhatsApp] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [hasEditedMessage, setHasEditedMessage] = useState(false);
  const [activeDescriptionLanguage, setActiveDescriptionLanguage] = useState('original');

  const messageSectionRef = useRef(null);
  const messageInputRef = useRef(null);

  const jobService = useMemo(() => new JobService(), []);
  const applicationService = useMemo(() => new ApplicationService(user), [user]);
  const presentation = useMemo(() => buildJobPresentation(job), [job]);
  const returnTo =
    typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.search || ''}`
      : `/jobs/${jobId}/apply`;

  const isUnpublished = job?.isPublished === false;
  const jobSeekerAccountStatus = userTypeData?.profiles?.jobSeeker?.accountStatus || 'active';
  const isSuspended = jobSeekerAccountStatus === 'suspended';
  const companyErrorMessage = 'Companies cannot apply for jobs.';
  const availableTranslationOptions = useMemo(
    () => TRANSLATION_OPTIONS.filter((option) => presentation?.translations?.[option.key]),
    [presentation]
  );
  const displayedDescription = useMemo(() => {
    if (activeDescriptionLanguage === 'original') {
      return presentation?.originalDescription || '';
    }

    return presentation?.translations?.[activeDescriptionLanguage] || presentation?.originalDescription || '';
  }, [activeDescriptionLanguage, presentation]);
  const shouldShowCategoryChip = useMemo(() => {
    const normalizedCategory = humanizeToken(presentation?.category || '').toLowerCase();
    const normalizedTitle = humanizeToken(presentation?.title || '').toLowerCase();
    return Boolean(normalizedCategory) && normalizedCategory !== normalizedTitle;
  }, [presentation]);

  useEffect(() => {
    let cancelled = false;

    const fetchJob = async () => {
      setIsJobLoading(true);
      setLoadError('');

      try {
        const jobData = await jobService.getJobApplyById(jobId);
        if (cancelled) return;

        setJob(jobData);
        posthog?.capture('job_viewed', {
          job_id: jobId,
          job_reference: jobData?.referenceCode,
          surface: 'job_apply',
        });
      } catch (error) {
        if (cancelled) return;
        setLoadError(error.message || 'Failed to load job details.');
      } finally {
        if (!cancelled) {
          setIsJobLoading(false);
        }
      }
    };

    fetchJob();
    return () => {
      cancelled = true;
    };
  }, [jobId, jobService, posthog]);

  useEffect(() => {
    setApplicationMessage('');
    setJobSeekerProfile(null);
    setHasEditedMessage(false);
    setSubmitError('');
    setIsConfirmDialogOpen(false);
    setIsOpeningWhatsApp(false);
    setActiveDescriptionLanguage('original');
  }, [jobId, user?.sub]);

  useEffect(() => {
    if (!isAuthenticated || !user || userTypeLoading) return;

    if (!isProfileDefined) {
      navigate(`/onboarding?returnTo=${encodeURIComponent(returnTo)}`);
    }
  }, [isAuthenticated, isProfileDefined, navigate, returnTo, user, userTypeLoading]);

  useEffect(() => {
    if (!isAuthenticated || !user || userTypeLoading || !isProfileDefined || isCompany || !job) {
      return;
    }

    const rawProfile = userTypeData?.profiles?.jobSeeker;
    if (!rawProfile) return;

    const profileData = {
      ...rawProfile,
      ...(rawProfile.basicProfile || {}),
      id: rawProfile.id,
    };

    setJobSeekerProfile(profileData);

    if (!hasEditedMessage) {
      setApplicationMessage(generateApplicationMessage(job, profileData));
    }
  }, [
    hasEditedMessage,
    isAuthenticated,
    isCompany,
    isProfileDefined,
    job,
    presentation,
    user,
    userTypeData,
    userTypeLoading,
  ]);

  const triggerErrorFeedback = (message) => {
    setSubmitError(message);
    setShowErrorAnimation(true);
    setTimeout(() => setShowErrorAnimation(false), 500);
  };

  const scrollToMessage = () => {
    messageSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => {
      messageInputRef.current?.focus();
    }, 350);
  };

  const handleLogin = () => {
    posthog?.capture('job_apply_login_clicked', { job_id: jobId });
    loginWithRedirect({
      appState: {
        redirect_uri: returnTo,
      },
    });
  };

  const handleSignup = () => {
    posthog?.capture('job_apply_signup_clicked', { job_id: jobId });
    signupWithRedirect({
      appState: {
        redirect_uri: returnTo,
      },
    });
  };

  const handleMessageChange = (event) => {
    setHasEditedMessage(true);
    setApplicationMessage(event.target.value);
  };

  const handleSubmit = (event) => {
    event?.preventDefault();
    if (!isAuthenticated) {
      posthog?.capture('job_apply_clicked', { job_id: jobId, job_reference: job?.referenceCode });
      handleLogin();
      return false;
    }

    if (isCompany) {
      triggerErrorFeedback('Companies cannot apply for jobs. Only job seekers can apply for positions.');
      return false;
    }

    if (isSuspended) {
      triggerErrorFeedback('Your account is suspended. Contact support to restore job applications.');
      return false;
    }

    if (isUnpublished) {
      triggerErrorFeedback('This job is no longer available.');
      return false;
    }

    if (!jobSeekerProfile?.id) {
      triggerErrorFeedback('Complete your job seeker profile before applying.');
      return false;
    }

    if (!applicationMessage.trim()) {
      triggerErrorFeedback('Please enter an application message.');
      scrollToMessage();
      return false;
    }

    setSubmitError('');
    setIsConfirmDialogOpen(true);
    return true;
  };

  const handleConfirmSubmit = async () => {
    setIsConfirmDialogOpen(false);
    setIsSubmitting(true);
    setSubmitError('');

    const pendingWindow = window.open('', '_blank');
    let openedWhatsApp = false;

    if (pendingWindow && pendingWindow.document) {
      pendingWindow.document.write(`
        <html>
          <head><title>Opening WhatsApp...</title></head>
          <body style="margin:0;font-family:Arial,sans-serif;background:#0a0f0c;color:#ebebdf;display:grid;place-items:center;min-height:100vh;">
            <div style="text-align:center;padding:24px;">
              <div style="font-size:18px;font-weight:700;margin-bottom:8px;">Opening WhatsApp...</div>
            </div>
          </body>
        </html>
      `);
      pendingWindow.document.close();
    }

    try {
      const hasApplied = await applicationService.hasUserAppliedForJob(jobId, jobSeekerProfile.id);
      if (hasApplied) {
        throw new Error('You have already applied for this job.');
      }

      const response = await applicationService.createApplication({
        jobId,
        jobSeekerProfileId: jobSeekerProfile.id,
        applicationMessage: applicationMessage.trim(),
        userId: user.sub,
        status: 'pending',
      });

      const createdApplication = response?.application || response;
      const whatsappUrl = response?.whatsappRedirect?.url;

      if (!createdApplication) {
        throw new Error('Failed to submit application. Please try again.');
      }

      if (!whatsappUrl) {
        throw new Error('Application saved, but WhatsApp could not be prepared.');
      }

      posthog?.capture('job_application_submitted', {
        job_id: jobId,
        job_reference: job?.referenceCode,
        destination: 'whatsapp',
      });

      setIsOpeningWhatsApp(true);
      openedWhatsApp = true;

      if (pendingWindow && !pendingWindow.closed) {
        pendingWindow.location.replace(whatsappUrl);
      } else {
        const whatsappWindow = window.open(whatsappUrl, '_blank');
        if (!whatsappWindow || whatsappWindow.closed || typeof whatsappWindow.closed === 'undefined') {
          window.location.href = whatsappUrl;
          return;
        }
      }

      setTimeout(() => {
        navigate('/jobs');
      }, 1000);
    } catch (error) {
      if (pendingWindow && !pendingWindow.closed) {
        pendingWindow.close();
      }
      triggerErrorFeedback(error.message || 'Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
      if (!openedWhatsApp) {
        setIsOpeningWhatsApp(false);
      }
    }
  };

  const renderApplyPanel = () => {
    if (!isAuthenticated) {
      return null;
    }

    if (userTypeLoading && !jobSeekerProfile) {
      return (
        <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size={30} />
          <Typography variant="body2" color="text.secondary">
            Preparing your Labourando profile...
          </Typography>
        </Stack>
      );
    }

    if (isCompany) {
      return (
        <Alert severity="error">
          {companyErrorMessage}
        </Alert>
      );
    }

    if (!isProfileDefined) {
      return (
        <Stack spacing={2}>
          <Alert severity="info">Redirecting you to onboarding so you can complete your job seeker profile.</Alert>
          <Button
            variant="contained"
            onClick={() => navigate(`/onboarding?returnTo=${encodeURIComponent(returnTo)}`)}
          >
            Complete profile
          </Button>
        </Stack>
      );
    }

    if (isSuspended) {
      return (
        <Alert severity="error">
          Your account is suspended and job applications are currently disabled. Please contact support.
        </Alert>
      );
    }

    if (isUnpublished) {
      return (
        <Stack spacing={2}>
          <Alert severity="warning">
            This job is no longer available. Browse the latest openings on the Jobs page.
          </Alert>
          <Button variant="outlined" onClick={() => navigate('/jobs')}>
            Browse jobs
          </Button>
        </Stack>
      );
    }

    return (
      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={2.5}>
          <Typography variant="body2" color="text.secondary">
            Your profile link and contact details are already included. Edit the message if you want to make it more personal.
          </Typography>

          {submitError ? (
            <Alert
              severity="error"
              sx={{
                animation: showErrorAnimation ? 'shake 0.5s ease-in-out' : 'none',
                border: '1px solid',
                borderColor: 'error.main',
              }}
            >
              {submitError}
            </Alert>
          ) : null}

          {isOpeningWhatsApp ? (
            <Alert severity="success">
              Opening WhatsApp with your pre-filled message.
            </Alert>
          ) : null}

          <Box
            ref={messageSectionRef}
            sx={{
              p: { xs: 1.5, md: 2 },
              borderRadius: 3,
              border: '1px solid',
              borderColor: alpha(theme.palette.primary.main, 0.18),
              bgcolor: alpha(theme.palette.primary.main, 0.04),
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.25 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                Your application message
              </Typography>
              <Chip
                size="small"
                label={`${applicationMessage.trim().length} chars`}
                sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.16), fontWeight: 700 }}
              />
            </Stack>

            <TextField
              inputRef={messageInputRef}
              multiline
              rows={9}
              fullWidth
              value={applicationMessage}
              onChange={handleMessageChange}
              placeholder="Write a short message that tells the employer why you are a good fit."
              label="Message to employer"
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'background.input',
                },
              }}
            />
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button
              variant="outlined"
              onClick={() => navigate('/jobs')}
              disabled={isSubmitting || isOpeningWhatsApp}
              sx={{ minHeight: 52, fontWeight: 700 }}
            >
              Back to jobs
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={isSubmitting || isOpeningWhatsApp ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
              disabled={isSubmitting || isOpeningWhatsApp || !applicationMessage.trim()}
              sx={{ minHeight: 56, flex: 1, fontSize: '1rem', fontWeight: 900 }}
            >
              {isSubmitting ? 'Saving application...' : isOpeningWhatsApp ? 'Opening WhatsApp...' : 'Apply via WhatsApp'}
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  };

  if (isJobLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Stack spacing={3} alignItems="center">
          <CircularProgress size={52} />
          <Typography>Loading job details...</Typography>
        </Stack>
      </Container>
    );
  }

  if (!job) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Stack spacing={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/jobs')}
            sx={{ alignSelf: 'flex-start' }}
          >
            Back to jobs
          </Button>
          <Alert severity="error">{loadError || 'Job not found.'}</Alert>
        </Stack>
      </Container>
    );
  }

  return (
    <>
      <GlobalStyles
        styles={{
          '@keyframes shake': {
            '0%, 100%': { transform: 'translateX(0)' },
            '20%': { transform: 'translateX(-4px)' },
            '40%': { transform: 'translateX(4px)' },
            '60%': { transform: 'translateX(-4px)' },
            '80%': { transform: 'translateX(4px)' },
          },
          '@keyframes riseIn': {
            from: { opacity: 0, transform: 'translateY(16px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
          },
        }}
      />

      <Box
        sx={{
          minHeight: '100vh',
          background: `
            radial-gradient(circle at top left, ${alpha(theme.palette.primary.main, 0.18)} 0%, transparent 34%),
            radial-gradient(circle at top right, ${alpha(theme.palette.secondary.main, 0.12)} 0%, transparent 28%),
            ${theme.palette.background.default}
          `,
        }}
      >
        <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 }, pb: { xs: 12, md: 5 } }}>
          <Stack spacing={{ xs: 2, md: 2.5 }} sx={{ animation: 'riseIn 0.5s ease-out' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/jobs')}
                sx={{ alignSelf: 'flex-start' }}
              >
                Back to jobs
              </Button>
            </Stack>

            <SectionCard>
              <Stack spacing={1.75}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', md: 'flex-start' }}
                  spacing={1.25}
                >
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 900,
                        lineHeight: 1.02,
                        fontSize: { xs: '1.65rem', md: '1.82rem' },
                      }}
                    >
                      {presentation?.title}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {shouldShowCategoryChip ? (
                      <Chip
                        icon={<WorkOutlineIcon />}
                        label={presentation?.category || 'General labour'}
                        sx={{ fontWeight: 800 }}
                      />
                    ) : null}
                    {presentation?.urgencyScore !== null && presentation?.urgencyScore >= 7 ? (
                      <Chip
                        icon={<BoltIcon />}
                        label="Urgent hiring"
                        color="secondary"
                        sx={{ fontWeight: 800 }}
                      />
                    ) : null}
                    {job.referenceCode ? (
                      <Chip label={job.referenceCode} variant="outlined" sx={{ fontWeight: 700 }} />
                    ) : null}
                  </Stack>
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} useFlexGap flexWrap="wrap">
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <AccessTimeIcon fontSize="small" color="primary" />
                    <Typography variant="body2" color="text.secondary">
                      {job.createdAt ? formatWhatsAppTime(job.createdAt) : 'Recently posted'}
                    </Typography>
                  </Stack>
                </Stack>

                {presentation?.locationLabel || presentation?.rateLabel || presentation?.startDateLabel ? (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
                    {presentation?.locationLabel ? (
                      <SimpleMetaItem
                        icon={<LocationOnIcon fontSize="small" />}
                        label="Location"
                        value={presentation.locationLabel}
                      />
                    ) : null}
                    {presentation?.rateLabel ? (
                      <SimpleMetaItem icon={<BoltIcon fontSize="small" />} label="Rate" value={presentation.rateLabel} />
                    ) : null}
                    {presentation?.startDateLabel ? (
                      <SimpleMetaItem
                        icon={<CalendarMonthIcon fontSize="small" />}
                        label="Start date"
                        value={presentation.startDateLabel}
                      />
                    ) : null}
                  </Stack>
                ) : null}
              </Stack>
            </SectionCard>

            <Stack spacing={3}>
              <SectionCard eyebrow="Description">
                <Stack spacing={2.5}>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.75, textAlign: 'left' }}>
                    {displayedDescription || 'No job description available.'}
                  </Typography>

                  {availableTranslationOptions.length ? (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip
                        icon={<LanguageIcon fontSize="small" />}
                        label="Original"
                        clickable
                        color={activeDescriptionLanguage === 'original' ? 'secondary' : 'default'}
                        onClick={() => setActiveDescriptionLanguage('original')}
                        sx={{ fontWeight: 700 }}
                      />
                      {availableTranslationOptions.map((option) => (
                        <Chip
                          key={option.key}
                          label={option.label}
                          clickable
                          color={activeDescriptionLanguage === option.key ? 'secondary' : 'default'}
                          onClick={() => setActiveDescriptionLanguage(option.key)}
                          sx={{ fontWeight: 700 }}
                        />
                      ))}
                    </Stack>
                  ) : null}

                  {presentation?.requirementsList?.length ? (
                    <Box>
                      <Typography variant="overline" sx={SECTION_EYEBROW_SX}>
                        Requirements
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {presentation.requirementsList.map((item) => (
                          <Chip
                            key={item}
                            icon={<ChecklistRoundedIcon />}
                            label={item}
                            sx={{ fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.12) }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  ) : null}

                  {presentation?.tickets?.length ? (
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 1 }}>
                        Tickets and licences
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {presentation.tickets.map((item) => (
                          <Chip key={item} label={humanizeToken(item)} variant="outlined" />
                        ))}
                      </Stack>
                    </Box>
                  ) : null}

                  {presentation?.missingInfo?.length ? (
                    <Alert severity="info">
                      Ask about these details in WhatsApp: {presentation.missingInfo.slice(0, 4).join(', ')}.
                    </Alert>
                  ) : null}
                </Stack>
              </SectionCard>

              {isAuthenticated ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, md: 3 },
                    borderRadius: 4,
                    border: '1px solid',
                    borderColor: alpha(theme.palette.primary.main, 0.18),
                    bgcolor: 'background.paper',
                    boxShadow: '0 20px 48px rgba(7, 44, 22, 0.12)',
                    width: '100%',
                  }}
                >
                  <Stack spacing={2.5}>
                    <Box>
                      <Typography variant="overline" sx={SECTION_EYEBROW_SX}>
                        Apply
                      </Typography>
                    </Box>

                    {renderApplyPanel()}
                  </Stack>
                </Paper>
              ) : null}

              {!isAuthenticated ? (
                <SectionCard eyebrow="Ready to apply?">
                  <Stack
                    direction="column"
                    spacing={2}
                    alignItems="center"
                    textAlign="center"
                  >
                    <Box sx={{ maxWidth: 560 }}>
                      <Typography variant="h5" sx={{ fontWeight: 900, mb: 0.5 }}>
                        Sign in to apply for this job
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Sign in with your Labourando job seeker profile to personalise your message and send your
                        application directly to the employer.
                      </Typography>
                    </Box>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                      <Button
                        variant="contained"
                        onClick={handleLogin}
                        sx={{ minHeight: 52, fontWeight: 800 }}
                      >
                        Sign in
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={handleSignup}
                        sx={{ minHeight: 52, fontWeight: 800 }}
                      >
                        Create free account
                      </Button>
                    </Stack>
                  </Stack>
                </SectionCard>
              ) : null}
            </Stack>
          </Stack>
        </Container>

        {!isCompany && !isSuspended && !isUnpublished ? (
          <Box
            sx={{
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 0,
              p: 1.5,
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: alpha(theme.palette.background.paper, 0.96),
              backdropFilter: 'blur(10px)',
              display: { xs: 'block', md: 'none' },
              zIndex: 1200,
            }}
          >
{!isAuthenticated ? (
                <Stack spacing={1.25}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      posthog?.capture('job_apply_clicked', { job_id: jobId, job_reference: job?.referenceCode, surface: 'mobile_footer' });
                      handleLogin();
                    }}
                    sx={{ minHeight: 52, fontWeight: 900 }}
                  >
                    Sign in to apply
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleSignup}
                    sx={{ minHeight: 52, fontWeight: 800 }}
                  >
                    Create free account
                  </Button>
                </Stack>
              ) : isProfileDefined ? (
              <Stack direction="row" spacing={1.25}>
                <Button
                  variant="outlined"
                  onClick={scrollToMessage}
                  sx={{ minHeight: 52, flex: 1, fontWeight: 700 }}
                >
                  Edit Message
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={isSubmitting || isOpeningWhatsApp || !applicationMessage.trim()}
                  startIcon={isSubmitting || isOpeningWhatsApp ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                  sx={{ minHeight: 52, flex: 1.4, fontWeight: 900 }}
                >
                  {isSubmitting ? 'Saving...' : isOpeningWhatsApp ? 'Opening...' : 'Apply'}
                </Button>
              </Stack>
            ) : null}
          </Box>
        ) : null}

        <Dialog
          open={isConfirmDialogOpen}
          onClose={() => {
            if (!isSubmitting && !isOpeningWhatsApp) {
              setIsConfirmDialogOpen(false);
            }
          }}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle sx={{ fontWeight: 900 }}>Continue to WhatsApp?</DialogTitle>
          <DialogContent>
            <DialogContentText color="text.primary">
              You will be redirected to the WhatsApp application with your message pre-filled.
            </DialogContentText>
            <DialogContentText sx={{ mt: 1.5 }}>
              Your application will only be completed after you send the message to the employer in WhatsApp.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button
              onClick={() => setIsConfirmDialogOpen(false)}
              disabled={isSubmitting || isOpeningWhatsApp}
              sx={{ fontWeight: 700 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmSubmit}
              variant="contained"
              disabled={isSubmitting || isOpeningWhatsApp}
              startIcon={isSubmitting || isOpeningWhatsApp ? <CircularProgress size={18} color="inherit" /> : <WhatsAppIcon />}
              sx={{ fontWeight: 800 }}
            >
              Continue
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default JobApply;
